import React from 'react';
import { ContactsOrderBy } from '@dev/ee-api/ee-api-models';
import { CheckboxTableHeaderProps } from '@dev/ee-components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import useMobile from '@/services/hooks/useMobile';
import { getGeneralInfo } from '@/store/account/actions';
import { getContactsCounts } from '@/store/contacts/actions';

import { DesktopStatusFilter } from '../components/DesktopStatusFilter';
import { useContactQueryParams, useFilterActions } from '../../../hooks';

export const useHeadersOfContactsListTable = <T extends CheckboxTableHeaderProps[] | string[]>(): T => {
    const { t } = useTranslation();
    const { queryOrderBy } = useContactQueryParams();
    const { EnableContactFeatures } = useSelector(getGeneralInfo);
    const { sortByAddDate, sortByUpdateDate } = useFilterActions();
    const { totalCount: totalContactCount } = useSelector(getContactsCounts);
    const isMobile = useMobile();

    if (isMobile) {
        const headerName = [t('status'), t('sent'), t('opened'), t('clicked'), t('invalid')];
        if (!EnableContactFeatures) {
            headerName.splice(1, 3);
        }
        return headerName as T;
    }

    const headerName: CheckboxTableHeaderProps[] = [
        {
            disablePadding: true,
            id: 'email',
            label: t('email'),
            numeric: false,
        },
        {
            disablePadding: false,
            filterIcon: false,
            id: 'status',
            label: t('status'),
            numeric: false,
            customContent: !!totalContactCount && <DesktopStatusFilter />,
        },
        {
            disablePadding: false,
            id: 'sent',
            label: t('sent'),
            numeric: true,
        },
        {
            disablePadding: false,
            id: 'opened',
            label: t('opened'),
            numeric: true,
        },
        {
            disablePadding: false,
            id: 'clicked',
            label: t('clicked'),
            numeric: true,
        },
        {
            disablePadding: false,
            id: 'bounced',
            label: t('invalid'),
            numeric: true,
        },
        {
            direction: queryOrderBy === ContactsOrderBy.DateAddedAsc ? ('asc' as const) : ('desc' as const),
            disablePadding: false,
            id: 'addedDate',
            label: t('added'),
            numeric: true,
            sortFunction: sortByAddDate,
            sortable: true,
        },
        {
            direction: queryOrderBy === ContactsOrderBy.DateUpdatedAsc ? ('asc' as const) : ('desc' as const),
            disablePadding: false,
            id: 'updatedDate',
            label: t('updated'),
            numeric: true,
            sortFunction: sortByUpdateDate,
            sortable: true,
        },
    ];
    if (!EnableContactFeatures) {
        headerName.splice(2, 4);
    }
    return headerName as T;
};
